import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Pencil, RefreshCw, UserPlus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure, initiales } from "@/lib/format";
import { useReferentiels } from "./ChoixListe";
import { UtilisateurDialog, copierLien } from "./UtilisateurDialog";
import { BoutonVisio } from "@/components/visio/BoutonVisio";

const Statut = ({ u }) => {
  const [label, couleur] = u.statut === "invite" ? ["Invité", "var(--temp-4)"] : u.actif ? ["Actif", "var(--success)"] : ["Désactivé", "var(--muted)"];
  return <span className="inline-flex items-center gap-1.5 text-sm font-semibold" data-testid={`user-status-${u.id}`}><span className="w-2 h-2 at-pill" style={{ background: couleur }} />{label}</span>;
};

const Ligne = ({ u, gerer, moi, onModifier, onRecharger }) => {
  const basculer = async (actif) => {
    try { await api.post(`/utilisateurs/${u.id}/statut`, { actif }); toast.success(actif ? "Utilisateur activé." : "Utilisateur désactivé."); onRecharger(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  const renvoyer = async () => {
    try { await api.post(`/utilisateurs/${u.id}/renvoyer-invitation`); toast.success("Invitation renvoyée."); onRecharger(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  return (
    <div data-testid={`user-row-${u.id}`} className="at-fiche p-4 md:grid md:grid-cols-[2.2fr_1.3fr_1fr_1.3fr_auto] md:items-center gap-4 space-y-3 md:space-y-0">
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-10 h-10 shrink-0 at-pill flex items-center justify-center font-bold text-sm" style={{ background: "var(--surface-2)" }}>{initiales(u)}</span>
        <div className="min-w-0"><p className="font-bold truncate">{u.prenom} {u.nom}</p><p className="text-sm at-muted truncate">{u.email}</p></div>
      </div>
      <div className="text-sm"><span className="font-semibold" data-testid={`user-role-${u.id}`}>{u.role_label}</span>{u.partenaire_nom && <span className="at-muted"> · {u.partenaire_nom}</span>}</div>
      <Statut u={u} />
      <div className="text-sm at-muted" data-testid={`user-last-login-${u.id}`}><span className="md:hidden">Dernière connexion : </span>{formatDateHeure(u.derniere_connexion)}</div>
      {gerer && (
        <div className="flex items-center gap-1 justify-end">
          {u.lien_invitation && <button type="button" className="at-icon-btn" aria-label="Copier le lien d'invitation" onClick={() => copierLien(u.lien_invitation)} data-testid={`user-copy-link-${u.id}`}><Copy size={17} /></button>}
          {u.statut === "invite" && <button type="button" className="at-icon-btn" aria-label="Renvoyer l'invitation" onClick={renvoyer} data-testid={`user-resend-${u.id}`}><RefreshCw size={17} /></button>}
          {u.id !== moi && u.actif && <BoutonVisio userId={u.id} label={null} testId={`user-visio-${u.id}`} className="at-icon-btn" />}
          <button type="button" className="at-icon-btn" aria-label="Modifier" onClick={() => onModifier(u)} data-testid={`user-edit-${u.id}`}><Pencil size={17} /></button>
          {u.id !== moi && (
            <label className="min-w-[56px] min-h-[44px] flex items-center justify-center cursor-pointer" data-testid={`user-toggle-zone-${u.id}`}>
              <Switch checked={u.actif} onCheckedChange={basculer} aria-label="Actif" data-testid={`user-toggle-${u.id}`}
                className="data-[state=checked]:bg-[var(--success)] data-[state=unchecked]:bg-[var(--line)]" />
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default function Utilisateurs() {
  const { user } = useAuth();
  const gerer = user.role === "dirigeant";
  const referentiels = useReferentiels();
  const [liste, setListe] = useState([]);
  const [dialogue, setDialogue] = useState({ ouvert: false, utilisateur: null });
  const charger = useCallback(() => api.get("/utilisateurs").then((r) => setListe(r.data)), []);
  useEffect(() => { charger(); }, [charger]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-base md:text-lg font-bold">Utilisateurs <span className="at-muted font-semibold">({liste.length})</span></h2>
        {gerer && <button type="button" className="at-btn at-btn-primary hidden md:inline-flex" onClick={() => setDialogue({ ouvert: true, utilisateur: null })} data-testid="invite-user-button"><UserPlus size={17} />Inviter</button>}
      </div>
      <div className="hidden md:grid grid-cols-[2.2fr_1.3fr_1fr_1.3fr_auto] gap-4 px-4 pb-2 at-label">
        <span>Utilisateur</span><span>Profil</span><span>Statut</span><span>Dernière connexion</span><span className="w-[180px]" />
      </div>
      <div className="space-y-2" data-testid="users-list">
        {liste.map((u) => <Ligne key={u.id} u={u} gerer={gerer} moi={user.id} onModifier={(x) => setDialogue({ ouvert: true, utilisateur: x })} onRecharger={charger} />)}
      </div>
      {gerer && (
        <button type="button" className="md:hidden at-btn at-btn-primary fixed bottom-[84px] left-4 right-4 z-30" onClick={() => setDialogue({ ouvert: true, utilisateur: null })} data-testid="invite-user-button-mobile">
          <UserPlus size={17} />Inviter un utilisateur
        </button>
      )}
      <UtilisateurDialog ouvert={dialogue.ouvert} setOuvert={(o) => setDialogue((d) => ({ ...d, ouvert: o }))} utilisateur={dialogue.utilisateur} referentiels={referentiels} onEnregistre={charger} />
    </div>
  );
}
